import cx from "classnames";
import moment from "moment";
import React, { memo, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Prompt } from "react-router";
import { Button, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane, UncontrolledTooltip } from "reactstrap";

import "react-widgets/dist/css/react-widgets.css";

import "./styles.scss";

import { ActionToolbar } from "./ActionToolbar";
import { Document } from "./Document";
import { browserSupportsEditing } from "./Document/InnodomContent/util";
import { KeyboardListener } from "./KeyboardListener";
import { Questions } from "./Questions";
import { reducer, useStateCallbacks } from "./state";
import { getDefaultState } from "./state/getDefaultState";
import { ConfidenceState, getConfidenceState, getOutputXml, valueMissingIdRef } from "./state/util";
import { PageMeta, Settings, State } from "./types";
import { ConfirmationModal } from "./ConfirmationModal";
import { OpenAi } from "./Openai";
import { ApiTaxonomy, WorkbenchTaxonomy } from "../../../common/api/v1.1/types/taxonomy";
import { TaxonomyApi } from "../../../common/api/v1.1/actions/TaxonomyApi";
import { Message } from "../../../common/components/Workbench/types";
import { JobWarning } from "../../../common/api/v1.1/types/jobs";
import { Warnings } from "./Warnings";

type Props = Readonly<{
  taxonomyAdmin: boolean;
  authToken: string;
  confidenceThreshold: number;
  imageUris: Array<string>;
  noteCount: number;
  notes: React.ReactElement;
  warnings: JobWarning[];
  onComplete: (xml: string) => Promise<void>;
  onReset: () => Promise<void>;
  onSave: (xml: string) => Promise<void>;
  resolveWarning: (warning: JobWarning) => Promise<void>;
  showMessage: (
    contents: React.ReactElement,
    title: string,
    displaySeconds?: number,
    icon?: Message["icon"],
  ) => void;  pageMeta: Array<PageMeta>;
  review: boolean;
  saving: boolean;
  settings: Settings;
  taxonomy: WorkbenchTaxonomy;
  xml: string;
  acceptAllOnSave: boolean;
}>;

moment.locale("en");
// tslint:disable-next-line: no-var-requires
const momentLocalizer = require("react-widgets-moment");
momentLocalizer();

enum ConfirmationModalType {
  openSubmitModal,
  openOverrideModal,
  openResetModal,
  openClearModal,
  closeAll,
}

const CONFIRM_HEADER = "Confirmation!";
const CONFIRM_BUTTON_LABEL = "Yes";
const CONFIRM_CLEAR_MESSAGE =
  "Are you sure you want to clear all data points? This will remove all predictions and user work.";
const CONFIRM_RESET_MESSAGE =
  "Are you sure you want to reset? You will lose all your existing work.";
const CONFIRM_SUBMIT_MESSAGE = "Are you sure you want to submit your work?";
const CONFIRM_SUBMIT_OVERRIDE_MESSAGE =
  "Some data points are missing referenced text. Are you sure you are ready to submit this work?";

// auto save every 30 seconds
const AUTO_SAVE_INTERVAL = 30 * 1000;

const MappingComponent = ({
  taxonomyAdmin,
  authToken,
  confidenceThreshold,
  imageUris,
  noteCount,
  notes,
  warnings,
  onReset,
  onComplete,
  onSave,
  showMessage,
  resolveWarning,
  pageMeta,
  review,
  saving,
  settings,
  taxonomy,
  xml,
  acceptAllOnSave,
}: Props) => {
  const showOpenAi = false;

  const [confirmation, setconfirmation] = useState(ConfirmationModalType.closeAll);
  const defaultState = useMemo(() => {
    const pages: State["pages"] = imageUris.map((src, index) => {
      const { height, width } = pageMeta[index];
      return {
        blocks: [],
        height,
        src,
        width,
      };
    });
    return getDefaultState(taxonomy, xml, pages, confidenceThreshold, authToken);
  }, []);
  const [state, dispatch] = useReducer(reducer, defaultState);
  const stateCallbacks = useStateCallbacks(review, dispatch, settings);
  const disableEditing = useMemo(() => !browserSupportsEditing(), []);
  const [tempSaving, setTempSaving] = useState(false);
  const onCompleteAction = async (): Promise<void> => {
    setconfirmation(ConfirmationModalType.openSubmitModal);
  };
  const handleCancel = (): void => {
    setconfirmation(ConfirmationModalType.closeAll);
  }
  const confirmSubmitModal = (): void => {
    setconfirmation(ConfirmationModalType.closeAll);
    if (valueMissingIdRef(state.values, state.blocks)) {
      setconfirmation(ConfirmationModalType.openOverrideModal);
    } else {
      submitForm();
    }
  };
  const confirmOverrideModal = (): void => {
    setconfirmation(ConfirmationModalType.closeAll);
    submitForm();
  };
  const submitForm = useCallback(async (): Promise<void> => {
    if (!!autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(undefined);
    }    
    window?.top?.postMessage({ action: 'submitWorkbench' }, '*');
    onComplete(getOutputXml(xml, state));
  }, [state, xml]);
  const confirmResetModal = useCallback(async (): Promise<void> => {
    setconfirmation(ConfirmationModalType.closeAll);
    onReset();
  }, [onReset]);
  const onResetAction = (): void => {
    setconfirmation(ConfirmationModalType.openResetModal);
  };
  const confirmClearModal = useCallback(async (): Promise<void> => {
    setconfirmation(ConfirmationModalType.closeAll);
    stateCallbacks.onClearAllDataPoints();
  }, [onReset]);
  const onClearAction = (): void => {
    setconfirmation(ConfirmationModalType.openClearModal);
  };
  const onSaveAction = useCallback(async (): Promise<void> => {
    window?.top?.postMessage({ action: 'saveWorkbench' }, '*');
    if (!state.modified) {
      return;
    }
    if (!!autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(undefined);
    }
    setTempSaving(true);
    setTimeout(async () => {
      await onSave(getOutputXml(xml, state));
      stateCallbacks.onMarkDataAsSaved();
      setTempSaving(false);
    }, 0);
  }, [onSave, setTempSaving, state, stateCallbacks.onMarkDataAsSaved, xml]);
  // implement an auto save function
  const [autoSaveTimer, setAutoSaveTimer] = useState<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );
  useEffect(() => {
    if (state.modified) {
      if (!autoSaveTimer) {
        setAutoSaveTimer(
          setTimeout(() => {
            setTempSaving(true);
            setTimeout(async () => {
              await onSave(getOutputXml(xml, state));
              stateCallbacks.onMarkDataAsSaved();
              setTempSaving(false);
            }, 0);
          }, AUTO_SAVE_INTERVAL),
        );
      }
    } else if (!!autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(undefined);
    }
    return () => {
      if (!!autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  });
  const onKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.trim().toLowerCase();
      switch (key) {
        case "tab": {
          e.preventDefault();
          if (e.shiftKey) {
            stateCallbacks.onMoveToPrevDataPoint();
          } else {
            stateCallbacks.onMoveToNextDataPoint();
          }
          return;
        }
        case "arrowdown": {
          e.preventDefault();
          stateCallbacks.onMoveToNextDataPoint();
          return;
        }
        case "arrowup": {
          e.preventDefault();
          stateCallbacks.onMoveToPrevDataPoint();
          return;
        }
        case "escape": {
          e.preventDefault();
          stateCallbacks.onClearActiveDataPoint();
          return;
        }
      }
    },
    [stateCallbacks],
  );
  const [activeTab, setActiveTab] = useState<"dataPoints" | "notes" | "openai" | "warnings">(
    warnings?.length > 0 ? "warnings" : "dataPoints"
  );

  let canSubmit : boolean = true;
  if (acceptAllOnSave) {
    Object.values(state.values).forEach((value: any) => {   
      const confidenceState = getConfidenceState(confidenceThreshold)(value);
      if (confidenceState === ConfidenceState.Low)
      {
        canSubmit = false;
      }
    })
  }  

  let confirmModalMessage: string = "";
  let handleConfirm: () => void;
  switch (confirmation) {
    case ConfirmationModalType.openSubmitModal: {
      confirmModalMessage = CONFIRM_SUBMIT_MESSAGE;
      handleConfirm = confirmSubmitModal;
      break;
    }
    case ConfirmationModalType.openOverrideModal: {
      confirmModalMessage = CONFIRM_SUBMIT_OVERRIDE_MESSAGE
      handleConfirm = confirmOverrideModal;
      break;
    }
    case ConfirmationModalType.openResetModal: {
      confirmModalMessage = CONFIRM_RESET_MESSAGE;
      handleConfirm = confirmResetModal;
      break;
    }
    default: {
      confirmModalMessage = CONFIRM_CLEAR_MESSAGE;
      handleConfirm = confirmClearModal;
    }
  }
  return (
    <>
      { confirmation !== ConfirmationModalType.closeAll &&  
          <ConfirmationModal 
            buttonLabel={CONFIRM_BUTTON_LABEL}
            confirmationHeader={CONFIRM_HEADER}
            confirmationMessage={confirmModalMessage}
            handleCancel={handleCancel}
            handleConfirm={handleConfirm}
          />
      }
      <Prompt
        message="You have unsaved changes. Are you sure you want to leave the page?"
        when={state.modified}
      />
      <KeyboardListener onKeyPress={onKeyPress} />
      <Row className={cx("h-100", { review })}>
        <Col className="h-100 pr-0" xs={7}>
          <Document
            disableEditing={disableEditing}
            renderPages={imageUris.length > 0}
            review={review}
            settings={settings}
            state={state}
            stateCallbacks={stateCallbacks}
            canSubmit={canSubmit}
            onReset={onResetAction}
            onClear={onClearAction}
            disabled={!!saving}
          />
          {!review && (
            <ActionToolbar
              canSave={state.modified}
              canSubmit={canSubmit}
              disabled={!!saving}
              onComplete={onCompleteAction}
              onSave={onSaveAction}
              saving={tempSaving}
            />
          )}
        </Col>
        <Col className="border-left datapoints-wrapper d-flex flex-column h-100 pl-0" xs={5}>
          <div>
            <div className="save-taxo-button">
              {!!showOpenAi && <Button 
                id="save-taxi-button"
                disabled={!taxonomyAdmin}
                onClick={() => {
                  let api = new TaxonomyApi(state.authToken);
                  let apiTaxonomy : ApiTaxonomy = {
                    description: state.taxonomy.description,
                    id: state.taxonomy.id,
                    type: state.taxonomy.type,
                    taxonomyJson: JSON.stringify(state.taxonomy.taxonomyObject)
                  }
                  
                  api.save(apiTaxonomy).then(x => {
                    showMessage(
                      <>Taxonomy Saved</>,
                      "Taxonomy Saved",
                      3,
                      "success",
                    );
                  });
                }} 
                type="button">
                  {!taxonomyAdmin && (<UncontrolledTooltip
                        target={`save-taxi-button`}
                        placement="left"
                        fade={false}
                      >
                        Ask an admin for permission to save taxonomies
                  </UncontrolledTooltip>)}
                  Save Taxonomy
              </Button>}
            </div>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === "dataPoints" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("dataPoints");
                  }}
                >
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M19.5554 18L21.5554 19V3C21.5554 1.9 20.6554 1 19.5554 1H9.54542C8.44542 1 7.55542 1.9 7.55542 3H17.5554C18.6554 3 19.5554 3.9 19.5554 5V18ZM15.5554 5H5.55542C4.45542 5 3.55542 5.9 3.55542 7V23L10.5554 20L17.5554 23V7C17.5554 5.9 16.6554 5 15.5554 5Z" fill="#0296F2"/>
                  </svg>
                </NavLink>
              </NavItem>
              <NavItem>
              {!!(warnings && warnings.length > 0) &&<NavLink
                  className={activeTab === "warnings" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("warnings");
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="red">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/> 
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                  </svg>  
                </NavLink>}
              </NavItem>
              {!!(showOpenAi && settings.openaiApiKey) && <NavItem>
                <NavLink
                  className={activeTab === "openai" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("openai");
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="#616161"/>
                  </svg>
                  {noteCount > 0 && <> ({noteCount})</>}
                </NavLink>
              </NavItem>}
              <NavItem>
                <NavLink
                  className={activeTab === "notes" ? "active" : ""}
                  onClick={() => {
                    setActiveTab("notes");
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M21.9998 4C21.9998 2.9 21.0998 2 19.9998 2H3.99976C2.89976 2 1.99976 2.9 1.99976 4V16C1.99976 17.1 2.89976 18 3.99976 18H17.9998L21.9998 22V4Z" fill="#616161"/>
                  </svg>
                  {noteCount > 0 && <> ({noteCount})</>}
                </NavLink>
              </NavItem>
            </Nav>
          </div>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="dataPoints">
              <Questions 
                warnings={warnings} 
                review={review} 
                state={state} 
                stateCallbacks={stateCallbacks} 
                focusWarningsTab={() => setActiveTab("warnings")} />
            </TabPane>
            <TabPane tabId="warnings">
              <Warnings 
                review={review} 
                state={state} 
                stateCallbacks={stateCallbacks} 
                warnings={warnings} 
                resolveWarning={resolveWarning} 
                focusDataPoints={() => setActiveTab("dataPoints")}
              />
            </TabPane>
            <TabPane tabId="openai">
              <OpenAi review={review} state={state} stateCallbacks={stateCallbacks} apiKey={settings.openaiApiKey}/>
            </TabPane>
            <TabPane className="h-100" tabId="notes">
              {notes}
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </>
  );
};
MappingComponent.displayName = "Mapping";
export const Mapping = memo(MappingComponent);
